import { EyeOff, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { apiGet, apiPost, apiSend, ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

/**
 * PODGLĄD CAŁEGO `SPSConfig.XML` — okienko, w którym plik się POKAZUJE i UMIERA.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ PO CO TO JEST: portal czyta z tego pliku CZTERY pola — serwer, bazę, użytkownika i `VMID` —
 *  i to wystarcza, dopóki wszystko działa. Gdy nie działa, odpowiedź bywa w polu, którego nie
 *  modelujemy: `SQLServerTrustedConnection`, `SQLServerTimeout`, `DoReplication`, `ParameterSet`,
 *  cała sekcja `SPSImport`. Dotąd jedyną drogą do nich był dojazd albo pulpit zdalny.
 *
 *  ⚠️ TO TYLKO ODCZYT i to jest cała różnica względem okna zapisu. Zapis ZATRZYMUJE interfejs
 *  automatu; podgląd nie dotyka ani pliku, ani procesu — wolno go otworzyć w środku dnia.
 *
 *  ⚠️⚠️ HASŁO NIE PRZYCHODZI TU W ŻADNEJ POSTACI. Agent maskuje tajne pola NA MASZYNIE, przed
 *  wysyłką, i odmawia wysłać cokolwiek, gdy maska nie objęła układu pliku. Lista zamaskowanych
 *  pól jedzie osobno i JEST POKAZANA — bo „hasła nie ma w pliku" i „hasło jest, ale go nie
 *  widzisz" to dwie zupełnie różne diagnozy, a bez tej listy nie da się ich rozróżnić.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

type Stan = {
  status: string;
  note: string | null;
  error: string | null;
  sciezka: string | null;
  xml: string | null;
  zamaskowane: string[];
  znakow: number | null;
};

/**
 * Jak często okienko pyta portal o gotowy plik.
 *
 * ⚠️ 400 MS — ta sama liczba, co przy zrzucie ekranu, i z tego samego powodu: agent pyta portal
 * o zlecenia co 2 s, a sam odczyt pliku to ułamek sekundy, więc sekundowe odpytywanie dokładałoby
 * pół sekundy do rzeczy, która ma zająć dwie. Pętla gaśnie z komponentem.
 */
const ODSTEP_MS = 400;
/** Po tym czasie przestajemy pytać — dłuższe kręcenie kółkiem nie jest odpowiedzią. */
const CIERPLIWOSC_MS = 3 * 60 * 1000;

export function PodgladSpsConfig({
  maszyna,
  groupId,
  onClose,
}: {
  maszyna: { id: string; name: string } | null;
  groupId: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [stan, setStan] = useState<Stan | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  /** ⚠️ W refie, nie w stanie: sprzątanie przy zamknięciu musi widzieć AKTUALNY identyfikator. */
  const zadanieRef = useRef<string | null>(null);
  const maszynaRef = useRef<string | null>(null);

  const otwarte = maszyna !== null;

  useEffect(() => {
    if (maszyna === null) return;
    let zyje = true;
    const start = Date.now();
    let uchwyt: ReturnType<typeof setTimeout> | undefined;

    setStan(null);
    setBlad(null);
    maszynaRef.current = maszyna.id;

    const pytaj = async (id: string): Promise<void> => {
      try {
        const s = await apiGet<Stan>(`/machines/${maszyna.id}/spsconfig-podglad/${id}?groupId=${groupId}`);
        if (!zyje) return;
        setStan(s);
        if (s.status === 'GOTOWY' || s.status === 'FAILED' || s.status === 'WYGASL') return;
        if (Date.now() - start > CIERPLIWOSC_MS) {
          setBlad(t('spscfgview.timeout'));
          return;
        }
        uchwyt = setTimeout(() => void pytaj(id), ODSTEP_MS);
      } catch (e) {
        if (!zyje) return;
        setBlad(e instanceof ApiError ? e.message : String(e));
      }
    };

    void (async () => {
      try {
        const { taskId: id } = await apiPost<{ taskId: string; dolaczone: boolean }>(
          `/machines/${maszyna.id}/spsconfig-podglad?groupId=${groupId}`,
        );
        if (!zyje) return;
        zadanieRef.current = id;
        void pytaj(id);
      } catch (e) {
        if (!zyje) return;
        /** Odmowy z API są tu treścią, nie awarią: „agent milczy od 40 min" to pełna odpowiedź. */
        setBlad(e instanceof ApiError ? e.message : String(e));
      }
    })();

    return () => {
      zyje = false;
      if (uchwyt !== undefined) clearTimeout(uchwyt);
    };
  }, [maszyna, groupId, t]);

  /**
   * ⚠️ ZAMKNIĘCIE USUWA TREŚĆ Z API. Bez tego wisiałaby do wygaśnięcia — a nie ma powodu, żeby
   * konfiguracja infrastruktury klienta leżała w naszej bazie choćby minutę dłużej, niż ktoś
   * na nią patrzy.
   */
  const zamknij = useCallback(() => {
    const id = zadanieRef.current;
    const m = maszynaRef.current;
    zadanieRef.current = null;
    if (id !== null && m !== null) {
      void apiSend('DELETE', `/machines/${m}/spsconfig-podglad/${id}?groupId=${groupId}`).catch(() => {});
    }
    onClose();
  }, [groupId, onClose]);

  const gotowy = stan?.status === 'GOTOWY' && stan.xml !== null;
  const opis = (() => {
    if (blad !== null) return blad;
    if (stan === null) return t('spscfgview.ordering');
    if (stan.status === 'FAILED') return stan.error ?? t('spscfgview.failed');
    if (stan.status === 'WYGASL') return t('spscfgview.expired');
    if (gotowy) return `${stan.sciezka ?? ''} · ${stan.znakow ?? 0} zn.`;
    return stan.note ?? t('spscfgview.waiting');
  })();

  return (
    <Dialog open={otwarte} onOpenChange={(o) => !o && zamknij()}>
      {/*
        ⚠️ SZEROKOŚĆ NADPISANA RAZEM Z `max-w-none` — bazowy `DialogContent` ustawia KONKRETNE
        512 px, więc samo `max-w-*` nie robi nic. Ta sama pułapka złapała okno zrzutu ekranu
        (zgłoszone 26.08.2026: „okienko jest bardzo małe"). Plik ma linie po ~60 znaków
        z wcięciami, więc 56rem mieści go bez zawijania.
      */}
      <DialogContent
        title={`${t('spscfgview.title')} — ${maszyna?.name ?? ''}`}
        description={opis}
        className="w-[min(56rem,calc(100vw-2rem))] max-w-none"
      >
        {gotowy ? (
          <div className="flex flex-col gap-3">
            {/*
              ⚠️ INFORMACJA O MASKOWANIU STOI NAD TREŚCIĄ, NIE POD. Kto zobaczy najpierw plik,
              odczyta zasłonięte pole jako awarię odczytu. Kto zobaczy najpierw tę linijkę, wie,
              że to decyzja — i że hasła nie ma nie dlatego, że go w pliku brak.
            */}
            {stan.zamaskowane.length > 0 && (
              <p className="flex items-start gap-2 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs">
                <EyeOff className="mt-0.5 size-4 shrink-0" />
                <span>{t('spscfgview.masked').replace('{pola}', stan.zamaskowane.join(', '))}</span>
              </p>
            )}
            {/*
              ⚠️ `overflow-x-auto` NA WŁASNYM POJEMNIKU, a nie zawijanie linii. Plik jest XML-em
              z wcięciami; zawinięcie długiej wartości (ścieżki, listy) zmieniłoby jego układ
              na ekranie, a podgląd ma pokazywać plik taki, jaki NA MASZYNIE LEŻY.
            */}
            <pre className="max-h-[60vh] overflow-auto rounded-md border bg-muted/40 p-3 text-xs leading-relaxed">
              {stan.xml}
            </pre>
          </div>
        ) : (
          <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            {blad === null && stan?.status !== 'FAILED' && stan?.status !== 'WYGASL' && (
              <Loader2 className="size-4 animate-spin" />
            )}
            {opis}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={zamknij}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
